import React from 'react';
import {
  Box,
  Button,
  FormControl,
  FormHelperText,
  InputLabel,
  MenuItem,
  Select,
  TextField,
} from '@mui/material';
import { RefreshCcw, RotateCcw } from 'lucide-react';

import type { AiLogOrigin, AiLogVarianceFilter } from '../../../types/aiLog';

export type AiLogDatePreset = '1h' | '24h' | '7d' | '31d' | 'custom';

export type AiLogsFilterDraft = {
  preset: AiLogDatePreset;
  from: string;
  to: string;
  user: string;
  origin: AiLogOrigin | '';
  model: string;
  capabilityCode: string;
  status: string;
  variance: AiLogVarianceFilter | '';
};

type AiLogsFiltersProps = {
  draft: AiLogsFilterDraft;
  dateRangeTooLong: boolean;
  refreshing: boolean;
  onDraftChange: <K extends keyof AiLogsFilterDraft>(key: K, value: AiLogsFilterDraft[K]) => void;
  onPresetChange: (preset: AiLogDatePreset) => void;
  onApplyDateRange: () => void;
  onReset: () => void;
  onRefresh: () => void;
};

export const AiLogsFilters: React.FC<AiLogsFiltersProps> = ({
  draft,
  dateRangeTooLong,
  refreshing,
  onDraftChange,
  onPresetChange,
  onApplyDateRange,
  onReset,
  onRefresh,
}) => {
  return (
    <Box className="rounded-lg border border-gray-200 bg-white p-4 shadow-sm dark:border-slate-700 dark:bg-slate-800">
      <Box className="flex flex-wrap items-start gap-3">
        <FormControl size="small" className="w-40 bg-white dark:bg-slate-800">
          <InputLabel>Date range</InputLabel>
          <Select
            value={draft.preset}
            label="Date range"
            onChange={(event) => onPresetChange(event.target.value as AiLogDatePreset)}
          >
            <MenuItem value="1h">Last 1h</MenuItem>
            <MenuItem value="24h">Last 24h</MenuItem>
            <MenuItem value="7d">Last 7d</MenuItem>
            <MenuItem value="31d">Last 31d</MenuItem>
            <MenuItem value="custom">Custom</MenuItem>
          </Select>
        </FormControl>

        <TextField
          size="small"
          label="From"
          type="datetime-local"
          value={draft.from}
          onChange={(event) => onDraftChange('from', event.target.value)}
          className="w-56 bg-white dark:bg-slate-800"
          InputLabelProps={{ shrink: true }}
          error={dateRangeTooLong}
        />
        <TextField
          size="small"
          label="To"
          type="datetime-local"
          value={draft.to}
          onChange={(event) => onDraftChange('to', event.target.value)}
          className="w-56 bg-white dark:bg-slate-800"
          InputLabelProps={{ shrink: true }}
          error={dateRangeTooLong}
        />
        <Box>
          <Button
            variant="outlined"
            onClick={onApplyDateRange}
            disabled={dateRangeTooLong}
            className="h-10"
          >
            Apply
          </Button>
          {dateRangeTooLong && (
            <FormHelperText error>Date range must be 31 days or less.</FormHelperText>
          )}
        </Box>

        <TextField
          size="small"
          label="User"
          value={draft.user}
          onChange={(event) => onDraftChange('user', event.target.value)}
          className="w-56 bg-white dark:bg-slate-800"
          placeholder="email or numeric ID"
        />
        <FormControl size="small" className="w-44 bg-white dark:bg-slate-800">
          <InputLabel>Source</InputLabel>
          <Select
            value={draft.origin}
            label="Source"
            onChange={(event) => onDraftChange('origin', event.target.value as AiLogOrigin | '')}
          >
            <MenuItem value="">All</MenuItem>
            <MenuItem value="aife">aife UI</MenuItem>
            <MenuItem value="api_key">API key</MenuItem>
            <MenuItem value="code_agent">Coding agent</MenuItem>
            <MenuItem value="system">System</MenuItem>
          </Select>
        </FormControl>
        <TextField
          size="small"
          label="Model"
          value={draft.model}
          onChange={(event) => onDraftChange('model', event.target.value)}
          className="w-48 bg-white dark:bg-slate-800"
        />
        <TextField
          size="small"
          label="Capability"
          value={draft.capabilityCode}
          onChange={(event) => onDraftChange('capabilityCode', event.target.value)}
          className="w-48 bg-white dark:bg-slate-800"
        />
        <FormControl size="small" className="w-40 bg-white dark:bg-slate-800">
          <InputLabel>Status</InputLabel>
          <Select
            value={draft.status}
            label="Status"
            onChange={(event) => onDraftChange('status', event.target.value)}
          >
            <MenuItem value="">All</MenuItem>
            <MenuItem value="success">success</MenuItem>
            <MenuItem value="failed">failed</MenuItem>
            <MenuItem value="cancelled">cancelled</MenuItem>
            <MenuItem value="pending">pending</MenuItem>
          </Select>
        </FormControl>
        <FormControl size="small" className="w-72 bg-white dark:bg-slate-800">
          <InputLabel>Variance</InputLabel>
          <Select
            value={draft.variance}
            label="Variance"
            onChange={(event) =>
              onDraftChange('variance', event.target.value as AiLogVarianceFilter | '')
            }
          >
            <MenuItem value="">All</MenuItem>
            <MenuItem value="over">System higher than Cloudflare</MenuItem>
            <MenuItem value="under">System lower than Cloudflare</MenuItem>
            <MenuItem value="out_of_tolerance">Out of tolerance</MenuItem>
            <MenuItem value="unknown">No Cloudflare cost yet</MenuItem>
          </Select>
        </FormControl>

        <Button
          variant="outlined"
          startIcon={<RotateCcw size={16} />}
          onClick={onReset}
          className="h-10"
        >
          Reset
        </Button>
        <Button
          variant="outlined"
          startIcon={<RefreshCcw size={16} />}
          onClick={onRefresh}
          disabled={refreshing}
          className="h-10"
        >
          Refresh
        </Button>
      </Box>
    </Box>
  );
};
